import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { usePageTracking } from './utils/analytics';
import { applyTheme } from './utils/theme';
import Layout from './components/Layout';
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import CategoryLanding from './pages/CategoryLanding';
import CategoryRedirect from './pages/CategoryRedirect';
import ProductRedirect from './pages/ProductRedirect';
import { consentAllows, getConsent, onConsentChange } from './utils/consent';
import { loadIutePay } from './utils/iutepay';
import { useTranslation } from 'react-i18next';
import BuyLink from './pages/BuyLink';
import Checkout from './pages/Checkout';
import CartPage from './pages/CartPage';
import OrderConfirmation from './pages/OrderConfirmation';
import CustomerLogin from './pages/CustomerLogin';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Account from './pages/Account';
import Wishlist from './pages/Wishlist';
import AccountOrderDetail from './pages/AccountOrderDetail';
import OrderTracker from './pages/OrderTracker';
import About from './pages/About';
import FAQ from './pages/FAQ';
import Contact from './pages/Contact';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import CookiePolicy from './pages/CookiePolicy';
import Returns from './pages/Returns';
import Shipping from './pages/Shipping';
import Bundles from './pages/Bundles';
import NotFound from './pages/NotFound';
import ContentPage from './pages/ContentPage';
import ReviewRequestPage from './pages/ReviewRequestPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CustomerAuthProvider } from './context/CustomerAuthContext';
import { CartProvider } from './context/CartContext';
import { ThemeProvider } from './context/ThemeContext';
import { SiteSettingsProvider } from './context/SiteSettingsContext';
import ProtectedRoute from './components/ProtectedRoute';
import MaintenancePage from './components/MaintenancePage';
import ScrollToTop from './components/ScrollToTop';
import Toast from './components/Toast';
import { useSiteSettings } from './context/SiteSettingsContext';
import i18n from './i18n/config';
import { captureAttribution } from './utils/attribution';
import './App.css';
import { hasChosenLanguage } from './utils/languageChoice';

// Admin + driver routes are lazy-loaded so customers don't download
// the ~30-40% of the bundle they never visit. The webpack chunks land
// at /static/js/admin-* and load on demand when an operator/courier
// navigates into the admin or /driver sections. Customer-facing
// imports (Home, ProductDetail, etc.) stay eager so the first-paint
// path stays a single round-trip.
const AdminLogin = React.lazy(() => import('./pages/admin/AdminLogin'));
const AdminLayout = React.lazy(() => import('./components/admin/AdminLayout'));
const AdminProducts = React.lazy(() => import('./pages/admin/AdminProductsNew'));
const AdminCategories = React.lazy(() => import('./pages/admin/AdminCategoriesNew'));
const AdminOrders = React.lazy(() => import('./pages/admin/AdminOrdersNew'));
const AdminCarousels = React.lazy(() => import('./pages/admin/AdminCarousels'));
const AdminPopularProducts = React.lazy(() => import('./pages/admin/AdminPopularProducts'));
const AdminSettings = React.lazy(() => import('./pages/admin/AdminSettings'));
const AdminNotifications = React.lazy(() => import('./pages/admin/AdminNotifications'));
const AdminClients = React.lazy(() => import('./pages/admin/AdminClients'));
const AdminCustomerSegments = React.lazy(() => import('./pages/admin/AdminCustomerSegments'));
const AdminOperations = React.lazy(() => import('./pages/admin/AdminOperations'));
const AdminAttribution = React.lazy(() => import('./pages/admin/AdminAttribution'));
const AdminClientDetail = React.lazy(() => import('./pages/admin/AdminClientDetail'));
const AdminOrderDetail = React.lazy(() => import('./pages/admin/AdminOrderDetail'));
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const AdminCoupons = React.lazy(() => import('./pages/admin/AdminCoupons'));
const AdminReviews = React.lazy(() => import('./pages/admin/AdminReviews'));
const AdminPayments = React.lazy(() => import('./pages/admin/AdminPayments'));
const AdminReturns = React.lazy(() => import('./pages/admin/AdminReturns'));
const AdminQuotes = React.lazy(() => import('./pages/admin/AdminQuotes'));
const AdminBundles = React.lazy(() => import('./pages/admin/AdminBundles'));
const AdminTranslations = React.lazy(() => import('./pages/admin/AdminTranslations'));
const AdminAgeReview = React.lazy(() => import('./pages/admin/AdminAgeReview'));
const AdminMedia = React.lazy(() => import('./pages/admin/AdminMedia'));
const AdminOnboarding = React.lazy(() => import('./pages/admin/AdminOnboarding'));
const AdminMarketing = React.lazy(() => import('./pages/admin/AdminMarketing'));
const AdminSearchSharing = React.lazy(() => import('./pages/admin/AdminSearchSharing'));
const AdminMarginReport = React.lazy(() => import('./pages/admin/AdminMarginReport'));
const AdminInventory = React.lazy(() => import('./pages/admin/AdminInventory'));
const AdminReconciliation = React.lazy(() => import('./pages/admin/AdminReconciliation'));
const AdminSettingsHub = React.lazy(() => import('./pages/admin/AdminSettingsHub'));
const AdminThemeStyleSettings = React.lazy(() => import('./pages/admin/AdminThemeStyleSettings'));
const AdminThemeEditor = React.lazy(() => import('./pages/admin/AdminThemeEditor'));
const AdminThemeLibrary = React.lazy(() => import('./pages/admin/AdminThemeLibrary'));
const AdminContentPages = React.lazy(() => import('./pages/admin/AdminContentPages'));
const AdminContentPageEditor = React.lazy(() => import('./pages/admin/AdminContentPageEditor'));
const AdminNavigation = React.lazy(() => import('./pages/admin/AdminNavigation'));
const AdminContentEntries = React.lazy(() => import('./pages/admin/AdminContentEntries'));
const AdminShippingRates = React.lazy(() => import('./pages/admin/AdminShippingRates'));
const AdminFiscalExport = React.lazy(() => import('./pages/admin/AdminFiscalExport'));
const AdminHelp = React.lazy(() => import('./pages/admin/AdminHelp'));
const AdminStaff = React.lazy(() => import('./pages/admin/AdminStaff'));
const AdminStaffSetup = React.lazy(() => import('./pages/admin/AdminStaffSetup'));
const DriverLogin = React.lazy(() => import('./pages/driver/DriverLogin'));
const DriverDashboard = React.lazy(() => import('./pages/driver/DriverDashboard'));

/**
 * Applies the operator-configured `default_locale` setting once it loads,
 * but only for visitors who haven't already made an explicit language
 * choice (querystring, manual switch, or a prior detected preference —
 * all cached to localStorage by i18next-browser-languagedetector under
 * `i18nextLng`). This lets a merchant's configured default win on first
 * visit without ever overriding a returning visitor's own choice.
 */
function LocaleSync() {
  const { defaultLocale, loaded } = useSiteSettings();
  useEffect(() => {
    if (!loaded || !defaultLocale) return;
    // The store's default beats the browser locale unless the visitor chose.
    if (hasChosenLanguage()) return;
    i18n.changeLanguage(defaultLocale);
  }, [loaded, defaultLocale]);
  return null;
}

/**
 * Injects the gtag.js script tag once the operator's `ga4_measurement_id`
 * setting loads (PHASE 3). This used to be a static, hand-edited snippet in
 * index.html — that meant every merchant had to edit a file to opt in, and
 * a stale hardcoded ID lived in analytics.js's `send_to` regardless (see
 * DECISIONS.md). Now the whole thing is settings-driven: nothing loads
 * until an operator sets a real ID in Settings → General.
 */
function GA4Bootstrap() {
  const { ga4MeasurementId, marketingConsentRequired, loaded } = useSiteSettings();
  useEffect(() => {
    if (!loaded || !ga4MeasurementId) return;
    if (typeof window.gtag === 'function') return;

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
    // Google consent mode: when the store asks for consent, cookies wait for
    // the visitor's "Accept"; until then Analytics counts visits without them.
    if (marketingConsentRequired) {
      window.gtag('consent', 'default', consentState(getConsent() === 'granted'));
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${ga4MeasurementId}`;
    document.head.appendChild(script);

    window.gtag('js', new Date());
    window.gtag('config', ga4MeasurementId);
  }, [loaded, ga4MeasurementId, marketingConsentRequired]);
  useEffect(() => onConsentChange((value) => {
    if (typeof window.gtag === 'function') window.gtag('consent', 'update', consentState(value === 'granted'));
  }), []);
  return null;
}

const consentState = (granted) => {
  const v = granted ? 'granted' : 'denied';
  return { analytics_storage: v, ad_storage: v, ad_user_data: v, ad_personalization: v };
};

/**
 * Meta Pixel for Instagram/Facebook catalog ads ("Shop now" opens our
 * checkout). Loads only when a Pixel ID is set, and after consent when the
 * store asks for it. Shop events reach it through trackEvent (utils/analytics).
 */
function MetaPixelBootstrap() {
  const { metaPixelId, marketingConsentRequired, loaded } = useSiteSettings();
  const [allowed, setAllowed] = useState(() => consentAllows(marketingConsentRequired));
  useEffect(() => {
    setAllowed(consentAllows(marketingConsentRequired));
    return onConsentChange(() => setAllowed(consentAllows(marketingConsentRequired)));
  }, [marketingConsentRequired]);
  useEffect(() => {
    if (!loaded || !metaPixelId || !allowed || window.fbq) return;
    const fbq = function fbq() { fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments); };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    window.fbq = fbq;
    window._fbq = fbq;
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
    window.fbq('init', metaPixelId);
    window.fbq('track', 'PageView');
  }, [loaded, metaPixelId, allowed]);
  return null;
}

/**
 * IutePay instalments: IUTE's script and stylesheet on every storefront
 * page once the store switches it on (utils/iutepay.js), so product pages
 * and lists show the monthly price and checkout can open IUTE's modal.
 * The language follows the page.
 */
function IutePayBootstrap() {
  const { iutepay, loaded } = useSiteSettings();
  const { i18n: translations } = useTranslation();
  const { pathname } = useLocation();
  const lang = (translations.language || 'sq').slice(0, 2);
  const onStorefront = !pathname.startsWith('/admin') && !pathname.startsWith('/driver');
  useEffect(() => {
    if (!loaded || !iutepay || !onStorefront) return;
    loadIutePay({ apiBase: iutepay.apiBase, publicKey: iutepay.publicKey, lang })
      .then((iute) => iute.setLocale?.(lang))
      .catch(() => {}); // widgets stay empty; checkout says IutePay is unavailable
  }, [loaded, iutepay, onStorefront, lang]);
  return null;
}

/**
 * Applies the operator's color/font choices onto CSS custom properties
 * once settings load (PHASE 4). Same `applyTheme()` call that the admin
 * Theme page's live iframe preview uses with unsaved draft values — see
 * frontend/src/utils/theme.js for what "applying a theme" actually does.
 */
function ThemeBootstrap() {
  const { loaded, themePrimaryColor, themeAccentColor, themeFontPairing, themeDesignTokens } = useSiteSettings();
  useEffect(() => {
    if (!loaded) return;
    applyTheme(themeDesignTokens || {
      primaryColor: themePrimaryColor,
      accentColor: themeAccentColor,
      fontPairing: themeFontPairing,
    });
  }, [loaded, themePrimaryColor, themeAccentColor, themeFontPairing, themeDesignTokens]);

  return null;
}

function AttributionCapture() {
  const location = useLocation();
  useEffect(() => { captureAttribution(); }, [location.search]);
  return null;
}

function MaintenanceGate({ children }) {
  const { maintenanceMode } = useSiteSettings();
  const location = useLocation();
  // Maintenance mode hides the public storefront but always lets the
  // operator into /admin and the delivery crew into /driver — they
  // need to keep working through an outage announcement.
  const isAdmin = location.pathname.startsWith('/admin');
  const isDriver = location.pathname.startsWith('/driver');

  if (maintenanceMode && !isAdmin && !isDriver) {
    return <MaintenancePage />;
  }
  return children;
}

/**
 * Redirects a freshly-installed store's admin into the setup wizard until
 * `store_onboarded` is set. Only guards /admin/* routes (never the public
 * storefront or /driver) and explicitly excludes /admin/login (pre-auth)
 * and /admin/onboarding itself (would otherwise redirect-loop).
 */
function OnboardingGate({ children }) {
  const { storeOnboarded, loaded } = useSiteSettings();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const isExempt = location.pathname === '/admin/login' || location.pathname === '/admin/onboarding';

  if (loaded && isAdmin && !isExempt && !storeOnboarded) {
    return <Navigate to="/admin/onboarding" replace />;
  }
  return children;
}

/**
 * Maps each /admin/* leaf route to the staff permission area that
 * gates it, mirroring the backend's per-controller
 * `permissionDeniedResponse()` calls (see RequiresPermissionTrait).
 * Routes not listed here (dashboard, staff, onboarding, staff/setup)
 * have no area gate — dashboard is the common landing page for any
 * authenticated staff account, staff management is owner-only
 * (enforced separately via `isOwner`), and onboarding/setup are
 * pre-permission setup flows.
 */
const ADMIN_ROUTE_AREAS = {
  '/admin/orders': 'orders',
  '/admin/returns': 'returns',
  '/admin/quotes': 'quotes',
  '/admin/fiscal-export': 'orders',
  '/admin/products': 'products',
  '/admin/inventory': 'products',
  '/admin/reconciliation': 'orders',
  '/admin/categories': 'collections',
  '/admin/bundles': 'bundles',
  '/admin/popular': 'products',
  '/admin/age-review': 'products',
  '/admin/clients': 'customers',
  '/admin/customer-segments': 'customers',
  '/admin/operations': 'settings',
  '/admin/attribution': 'marketing',
  '/admin/coupons': 'discounts',
  '/admin/media': 'content',
  '/admin/carousels': 'content',
  '/admin/reviews': 'content',
  '/admin/pages': 'content',
  '/admin/navigation': 'content',
  '/admin/content': 'content',
  '/admin/marketing': 'marketing',
  '/admin/search-sharing': 'marketing',
  '/admin/settings': 'settings',
  '/admin/notifications': 'settings',
  '/admin/translations': 'settings',
  '/admin/settings-hub': 'settings',
  '/admin/footer-links': 'content',
  '/admin/shipping-rates': 'settings',
  '/admin/theme': 'settings',
  '/admin/themes': 'settings',
  '/admin/theme-style': 'settings',
  '/admin/reports': 'orders',
  '/admin/payments': 'payments',
};

/**
 * Blocks a staff account from reaching (or deep-linking into) an
 * admin page it lacks permission for. The server independently
 * enforces every one of these areas (StaffPermissionVoter +
 * per-controller checks) — this is purely a UX guard so a restricted
 * staff member is redirected to the dashboard instead of seeing a
 * page that will just 403 on every API call.
 */
function PermissionGate({ children }) {
  const { isOwner, hasPermission, loading } = useAuth();
  const location = useLocation();

  if (loading || isOwner) return children;

  // /admin/staff is owner-only (staff management itself), not gated by
  // an area in permissions[] — a non-owner deep-linking here would
  // otherwise just see a page full of 403 errors from the API. Note:
  // /admin/staff/setup is a separate top-level route (pre-auth, outside
  // this gate) — see App.js's route list.
  if (location.pathname.startsWith('/admin/staff')) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const matchedPrefix = Object.keys(ADMIN_ROUTE_AREAS).find(
    (path) => location.pathname === path || location.pathname.startsWith(path + '/')
  );
  const area = matchedPrefix ? ADMIN_ROUTE_AREAS[matchedPrefix] : null;

  if (area && !hasPermission(area)) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  return children;
}

/**
 * Light-touch guard for the /driver app. We don't have a dedicated
 * driver context (drivers reuse the same JWT as admins), so we just
 * decode the token client-side; if it carries ROLE_DRIVER (or
 * ROLE_ADMIN, which inherits driver via security.yaml's hierarchy)
 * we let them through, otherwise we bounce to the driver login.
 *
 * Server-side every /api/driver/* endpoint independently enforces
 * ROLE_DRIVER, so a tampered token can't grant access — this guard
 * is purely a UX optimisation to avoid flashing the dashboard to
 * an unauthorised user.
 */
function DriverGuard({ children }) {
  // Driver app uses its own JWT storage key (`driver_token`) so admins
  // can be signed in to /admin and /driver simultaneously without one
  // session clobbering the other. See DriverLogin.js for the rationale.
  const token = typeof window !== 'undefined' ? localStorage.getItem('driver_token') : null;
  if (!token) return <Navigate to="/driver/login" replace />;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.exp * 1000 <= Date.now()) {
      localStorage.removeItem('driver_token');
      return <Navigate to="/driver/login" replace />;
    }
    const roles = payload?.roles || [];
    if (!roles.includes('ROLE_DRIVER') && !roles.includes('ROLE_ADMIN')) {
      return <Navigate to="/driver/login" replace />;
    }
  } catch {
    return <Navigate to="/driver/login" replace />;
  }
  return children;
}

// Minimal loading state for lazy chunks. Intentionally inline rather
// than a full-screen spinner — most chunks load in <100ms and a heavy
// loader would itself cause layout shift. The empty span keeps the
// route slot occupied while the chunk arrives.
const ChunkFallback = () => (
  <div style={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <span style={{ fontSize: '0.875rem', color: '#9ca3af' }}>Loading…</span>
  </div>
);

function AppRoutes() {
  usePageTracking();
  return (
    <>
      <ScrollToTop />
      <Toast />
      <LocaleSync />
      <GA4Bootstrap />
      <MetaPixelBootstrap />
      <IutePayBootstrap />
      <ThemeBootstrap />
      <AttributionCapture />
      <MaintenanceGate>
      <OnboardingGate>
      <React.Suspense fallback={<ChunkFallback />}>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="category/:slug" element={<CategoryRedirect landing={<CategoryLanding />} />} />
          <Route path="products" element={<Products />} />
          <Route path="products/:slug" element={<ProductDetail />} />
          <Route path="product/:slug" element={<ProductRedirect />} />
          <Route path="buy" element={<BuyLink />} />
          <Route path="buy/:slug" element={<BuyLink />} />
          <Route path="cart" element={<CartPage />} />
          <Route path="checkout" element={<Checkout />} />
          <Route path="order-confirmation/:orderNumber" element={<OrderConfirmation />} />
          <Route path="login" element={<CustomerLogin />} />
          <Route path="register" element={<Register />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password" element={<ResetPassword />} />
          <Route path="account" element={<Account />} />
          <Route path="wishlist" element={<Wishlist />} />
          <Route path="account/orders/:id" element={<AccountOrderDetail />} />
          <Route path="track/:token" element={<OrderTracker />} />
          <Route path="about" element={<About />} />
          <Route path="faq" element={<FAQ />} />
          <Route path="contact" element={<Contact />} />
          <Route path="terms" element={<Terms />} />
          <Route path="privacy" element={<Privacy />} />
          <Route path="cookies" element={<CookiePolicy />} />
          <Route path="returns" element={<Returns />} />
          <Route path="shipping" element={<Shipping />} />
          <Route path="bundles" element={<Bundles />} />
          <Route path="review-request/:token" element={<ReviewRequestPage />} />
          <Route path="page-preview" element={<ContentPage preview />} />
          <Route path=":slug" element={<ContentPage />} />
          <Route path="*" element={<NotFound />} />
        </Route>

        <Route path="/driver/login" element={<DriverLogin />} />
        <Route path="/driver" element={<DriverGuard><DriverDashboard /></DriverGuard>} />

        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/staff/setup" element={<AdminStaffSetup />} />
        <Route path="/admin" element={<ProtectedRoute><PermissionGate><AdminLayout /></PermissionGate></ProtectedRoute>}>
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="onboarding" element={<AdminOnboarding />} />
          <Route path="staff" element={<AdminStaff />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="inventory" element={<AdminInventory />} />
          <Route path="reconciliation" element={<AdminReconciliation />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="carousels" element={<AdminCarousels />} />
          <Route path="popular" element={<AdminPopularProducts />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="notifications" element={<AdminNotifications />} />
          <Route path="clients" element={<AdminClients />} />
          <Route path="customer-segments" element={<AdminCustomerSegments />} />
          <Route path="operations" element={<AdminOperations />} />
          <Route path="attribution" element={<AdminAttribution />} />
          <Route path="clients/:id" element={<AdminClientDetail />} />
          <Route path="coupons" element={<AdminCoupons />} />
          <Route path="reviews" element={<AdminReviews />} />
          <Route path="pages" element={<AdminContentPages />} />
          <Route path="pages/:id" element={<AdminContentPageEditor />} />
          <Route path="navigation" element={<AdminNavigation />} />
          <Route path="content" element={<AdminContentEntries />} />
          <Route path="payments" element={<AdminPayments />} />
          <Route path="returns" element={<AdminReturns />} />
          <Route path="quotes" element={<AdminQuotes />} />
          <Route path="bundles" element={<AdminBundles />} />
          <Route path="translations" element={<AdminTranslations />} />
          <Route path="age-review" element={<AdminAgeReview />} />
          <Route path="media" element={<AdminMedia />} />
          <Route path="marketing" element={<AdminMarketing />} />
          <Route path="search-sharing" element={<AdminSearchSharing />} />
          <Route path="reports" element={<AdminMarginReport />} />
          <Route path="settings-hub" element={<AdminSettingsHub />} />
          <Route path="theme" element={<AdminThemeEditor />} />
          <Route path="themes" element={<AdminThemeLibrary />} />
          <Route path="theme-style" element={<AdminThemeStyleSettings />} />
          <Route path="footer-links" element={<AdminNavigation />} />
          <Route path="shipping-rates" element={<AdminShippingRates />} />
          <Route path="fiscal-export" element={<AdminFiscalExport />} />
          <Route path="help" element={<AdminHelp />} />
          <Route path="orders/:id" element={<AdminOrderDetail />} />
        </Route>
      </Routes>
      </React.Suspense>
      </OnboardingGate>
    </MaintenanceGate>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <CustomerAuthProvider>
        <SiteSettingsProvider>
        <CartProvider>
          <ThemeProvider>
            <Router>
              <AppRoutes />
            </Router>
          </ThemeProvider>
        </CartProvider>
        </SiteSettingsProvider>
      </CustomerAuthProvider>
    </AuthProvider>
  );
}

export default App;
